import React, { useEffect, useRef, useState } from "react";
import Linkify from "react-linkify";
import { timestampToTime } from "../../utils/GeneralUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./ChatMessageBox.module.scss";
import ISonicMessage from "../../common/classes/SonicMessage";
import SonicLink from "../../common/routing/SonicLink";

type Props = {
  userId?: string;
  pastMessages: Array<ISonicMessage>;
  isFullHeight?: boolean;
  hasNext: boolean;
  clientEvent: string;
  handleLoad: (page: number) => void;
  readMessage?: (
    messageIds: Array<string>,
    messages: Array<ISonicMessage>
  ) => void;
  handleImgZoom?: (src: string) => void;
  setClientEvent: (value: string) => void;
};

const ChatMessageBox: React.FC<Props> = ({
  userId,
  pastMessages,
  isFullHeight = false,
  hasNext,
  clientEvent,
  handleLoad,
  readMessage,
  handleImgZoom,
  setClientEvent,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [position, setPosition] = useState<number>(0);

  const ref = useRef<HTMLUListElement>(null);

  const genInfo = (requestType: "offer" | "project", isOwner: boolean) => {
    switch (requestType) {
      case "offer":
        return isOwner ? "オファーがありました。" : "オファーをしました。";
      case "project": {
        return isOwner ? "に応募がありました。" : "に応募しました。";
      }
    }
  };

  useEffect(() => {
    if (ref && ref.current !== null) {
      ref.current.scrollTop = ref.current.scrollHeight;
      setPosition(ref.current.scrollHeight);
    }
  }, []);

  useEffect(() => {
    if (ref.current && hasNext && clientEvent === "load") {
      ref.current.scrollTop = ref.current.scrollHeight - position;
      setPosition(ref.current.scrollHeight);
    }

    if (clientEvent === "send") {
      ref.current?.scrollTo({ top: ref.current.scrollHeight });
    }

    setClientEvent("");

    const ul = document.getElementById("box");
    const li = document.getElementsByClassName("unread-message");

    if (!ul) return;

    const readJudgment = () => {
      if (userId && readMessage && ul && li) {
        const messageIds: Array<string> = [];

        const windowTop = ul?.getBoundingClientRect().top; //画面の高さを取得
        if (!windowTop) return;

        for (const target of li) {
          const msgId = target.getAttribute("data-msg-id");
          const message = pastMessages.find((msg) => msg.id === msgId);

          if (!msgId || !message || !message?.unreadUsers?.includes(userId))
            continue;

          const targetPos = target.getBoundingClientRect().top; //ターゲット要素の位置を取得
          if (targetPos > windowTop) {
            messageIds.push(msgId);
          }
        }

        readMessage(messageIds, pastMessages);
      }
    };

    ul.addEventListener("scroll", readJudgment);

    return () => {
      ul.removeEventListener("scroll", readJudgment);
    };
  }, [pastMessages]);

  useEffect(() => {
    ref?.current?.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      ref?.current?.removeEventListener("scroll", handleScroll);
    };
  }, [ref, currentPage]);

  const handleScroll = () => {
    if (ref.current === null || !hasNext || !handleLoad) return;

    const top = ref.current.scrollTop;
    if (top !== 0) return;

    handleLoad(currentPage + 1);
    setCurrentPage(currentPage + 1);
  };

  // 既読処理がスクロールイベントのため最初にスクロール可能か確認する
  useEffect(() => {
    const ul = document.getElementById("box");

    if (!ul) {
      return;
    }

    if (ul.scrollHeight > ul.clientHeight) {
      return;
    }

    if (!userId || !readMessage) {
      return;
    }

    const ids = pastMessages
      .filter((message) => message.unreadUsers.includes(userId))
      .map((message) => message.id);

    if (ids.length > 0) {
      readMessage(ids, pastMessages);
    }
  }, []);

  return (
    <div
      className={
        isFullHeight
          ? `${styles["f-chatMessageBox"]} ${styles["full-height"]}`
          : styles["f-chatMessageBox"]
      }
    >
      {pastMessages.length > 0 ? (
        <>
          <ul className={styles["f-chatMessageBox__list"]} ref={ref} id="box">
            {pastMessages.map((msg) =>
              msg.requestType ? (
                <li
                  key={msg.id}
                  className={
                    userId && msg.unreadUsers.includes(userId)
                      ? `${styles["f-chatMessageBox__list-item"]} ${styles["info"]} unread-message`
                      : `${styles["f-chatMessageBox__list-item"]} ${styles["info"]}`
                  }
                  data-msg-id={msg.id}
                >
                  <span>{timestampToTime(msg.sendDate, true, true)}</span>
                  <p>
                    {msg.title && (
                      <>
                        「<span>{msg.title}</span>」
                      </>
                    )}
                    {genInfo(msg.requestType, msg.relationOwner === userId)}
                  </p>
                </li>
              ) : (
                <li
                  key={msg.id}
                  className={
                    msg.senderId === userId
                      ? `${styles["f-chatMessageBox__list-item"]} ${styles["own"]}`
                      : userId && msg.unreadUsers.includes(userId)
                      ? `${styles["f-chatMessageBox__list-item"]} unread-message`
                      : `${styles["f-chatMessageBox__list-item"]}`
                  }
                  data-msg-id={msg.id}
                >
                  {msg.senderId !== userId && (
                    <span
                      className={styles["f-chatMessageBox__list-item__name"]}
                    >
                      <SonicLink
                        to={`/user/${msg.senderId}`}
                        className={styles["f-chatMessageBox__sender-link"]}
                        newTab={true}
                      >
                        {msg.senderName}
                      </SonicLink>
                    </span>
                  )}
                  {msg.img && (
                    <button
                      type="button"
                      onClick={() => handleImgZoom && handleImgZoom(msg.img)}
                    >
                      <img src={msg.img} />
                    </button>
                  )}
                  {msg.message && (
                    <div>
                      <Linkify
                        componentDecorator={(
                          decoratedHref,
                          decoratedText,
                          key
                        ) => (
                          <a target="blank" href={decoratedHref} key={key}>
                            {decoratedText}
                          </a>
                        )}
                      >
                        {msg.message}
                      </Linkify>
                    </div>
                  )}
                  <span className={styles["f-chatMessageBox__list-item__time"]}>
                    {timestampToTime(msg.sendDate, true, true)}
                  </span>
                </li>
              )
            )}
          </ul>
          {pastMessages.some((el) => {
            const u = el.unreadUsers.filter((id) => id === userId);
            return u.length > 0;
          }) && (
            <span className={styles["f-chatMessageBox__unread-alert"]}>
              <FontAwesomeIcon icon={["fas", "arrow-down"]} />
              未読メッセージがあります
            </span>
          )}
        </>
      ) : (
        <div className={styles["f-chatMessageBox__no-message"]}>
          メッセージはまだありません
        </div>
      )}
    </div>
  );
};

export default ChatMessageBox;
